"use client";

import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Copy } from "lucide-react";
import { TrashCanSVG } from "@/components/ui/SVG";
import { dragShadows, dragTransition, controlsSpring } from "./blockCard.constants";

type Props = {
  children: React.ReactNode;
  inputDir: "ltr" | "rtl";
  highlightBorder: boolean;
  isDragging: boolean;
  editMode: boolean;
  hideCopyAction: boolean;
  actionsMuted: boolean;
  copied: boolean;
  handleCopy: (e: React.MouseEvent) => void;
  hideDeleteControl: boolean;
  onRemove: () => void;
  isSelected: boolean;
  onSelect?: () => void;
  dragAttributes: Record<string, any>;
  dragListeners: Record<string, any>;
};

export function BlockCardShell({
  children,
  inputDir,
  highlightBorder,
  isDragging,
  editMode,
  hideCopyAction,
  actionsMuted,
  copied,
  handleCopy,
  hideDeleteControl,
  onRemove,
  isSelected,
  onSelect,
  dragAttributes,
  dragListeners,
}: Props) {
  const isDraggable =
    Object.keys(dragListeners ?? {}).length > 0 ||
    Object.keys(dragAttributes ?? {}).length > 0;
  return (
    <motion.div
      className={`relative rounded p-3 ${highlightBorder ? "border border-slate-200 bg-white" : ""}`}
      animate={{
        boxShadow: isDragging ? dragShadows.dragged : dragShadows.idle,
        y: isDragging ? -4 : 0,
      }}
      transition={dragTransition}
      dir={inputDir}
      style={{
        touchAction: isDraggable ? "none" : "pan-y",
        cursor: isDraggable ? (isDragging ? "grabbing" : "grab") : undefined,
        zIndex: isDragging ? 50 : undefined,
        position: "relative",
      }}
      onMouseDownCapture={(event) => {
        if (onSelect && !editMode) {
          event.stopPropagation();
          onSelect();
        }
      }}
      onClick={(event) => {
        if (onSelect && !editMode) {
          event.stopPropagation();
          onSelect();
        }
      }}
      role={onSelect ? "button" : undefined}
      {...dragAttributes}
      {...dragListeners}
    >
      {isSelected && (
        <>
          <span className="border-pinkCustom absolute top-0 left-0 h-2 w-2 rounded-tl-sm border-t border-l" />
          <span className="border-pinkCustom absolute top-0 right-0 h-2 w-2 rounded-tr-sm border-t border-r" />
          <span className="border-pinkCustom absolute bottom-0 left-0 h-2 w-2 rounded-bl-sm border-b border-l" />
          <span className="border-pinkCustom absolute right-0 bottom-0 h-2 w-2 rounded-br-sm border-r border-b" />
        </>
      )}

      {!editMode && !hideCopyAction ? (
        <button
          type="button"
          className={`pointer-events-auto absolute top-1 right-3 z-20 flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-gray-700 transition hover:bg-gray-100 ${actionsMuted ? "opacity-20 pointer-events-none" : ""}`}
          onClick={handleCopy}
          aria-label={copied ? "Block copied" : "Copy block"}
          title={copied ? "Copied!" : "Copy block JSON"}
        >
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
        </button>
      ) : null}

      <AnimatePresence>
        {editMode ? (
          <motion.div
            key="block-controls"
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={controlsSpring}
            className="pointer-events-auto absolute inset-x-0 top-1 z-20 flex justify-center gap-2"
          >
            {!hideDeleteControl ? (
              <motion.button
                type="button"
                className="flex items-center gap-1 rounded-md bg-red-600 px-3 py-1 text-xs font-medium text-white shadow hover:bg-red-500"
                style={actionsMuted ? { opacity: 0.2, pointerEvents: "none" } : undefined}
                whileTap={{ scale: 0.96 }}
                onClick={(event) => {
                  event.stopPropagation();
                  onRemove();
                }}
                aria-label="Delete block"
                title="Delete block"
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
              >
                <TrashCanSVG className="h-4 w-4" svgColor="white" accentColor="white" />
              </motion.button>
            ) : null}
          </motion.div>
        ) : null}
      </AnimatePresence>

      {children}
    </motion.div>
  );
}
